import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Users,
  LayoutDashboard,
  Settings,
  Building2,
  ScrollText,
  ShieldCheck,
  Utensils,
  Sparkles,
  DollarSign,
  Calendar,
  CheckCircle2,
  ShoppingBag,
  Calculator,
  BarChart3,
  Package,
  Bell,
  LifeBuoy
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { useTheme } from '../context/ThemeContext';
import { supabase } from '../supabase/config';
import { logActivity } from '../utils/activityLogger';

// Sub-components
import Sidebar from './layout/Sidebar';
import TopBar from './layout/TopBar';
import ProfileModal from './layout/ProfileModal';
import OrderAlert from './layout/OrderAlert';
import AIChatSidebar from './AIChatSidebar';
import SuperAdminAISidebar from './SuperAdminAISidebar';
import OnboardingGuide from './layout/OnboardingGuide';

const Layout = () => {
  const [isSidebarOpen, setSidebarOpen] = useState(() => window.innerWidth > 1024);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [newOrderAlert, setNewOrderAlert] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { theme, toggleMode } = useTheme();
  const {
    user, userData, tenantData, logout,
    selectedTenantId, resetTenant, effectiveTenantId,
    hasPermission, isSuperAdmin
  } = useAuth();
  const { showAlert, showConfirm } = useAlert();

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (window.innerWidth <= 1024) {
      document.body.style.overflow = isSidebarOpen ? 'hidden' : '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isSidebarOpen]);

  // Realtime Order Listener
  useEffect(() => {
    if (!effectiveTenantId || !user) return;

    let isInitialLoad = true;
    const channelName = `orders-pending-${effectiveTenantId}-${Math.random().toString(36).substring(7)}`;

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `tenant_id=eq.${effectiveTenantId}`
        },
        (payload) => {
          if (isInitialLoad) {
            isInitialLoad = false;
            return;
          }

          const row = payload.new;
          if (row && row.status === 'pending') {
            const orderData = {
              id: row.id,
              tenantId: row.tenant_id,
              customerId: row.customer_id,
              customerName: row.customer_name,
              tableNumber: row.table_number,
              status: row.status,
              items: row.items,
              totalAmount: row.total_amount,
              remainingAmount: row.remaining_amount,
              payments: row.payments,
              earnedPoints: row.earned_points,
              createdBy: row.created_by,
              timestamp: row.timestamp,
              updatedAt: row.updated_at,
              paidAt: row.paid_at,
            };
            setNewOrderAlert(orderData);

            try {
              new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3').play();
            } catch (e) {
              // Browser silent fail common before user interaction
            }

            setTimeout(() => setNewOrderAlert(null), 10000);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          isInitialLoad = false;
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error(`Order listener error for tenant ${effectiveTenantId}: ${status}`);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [effectiveTenantId, user]);

  // Show onboarding for first-time users
  useEffect(() => {
    if (!effectiveTenantId || isSuperAdmin) return;
    const key = `ugra_onboarding_done_${effectiveTenantId}`;
    if (!localStorage.getItem(key)) {
      setShowOnboarding(true);
    }
  }, [effectiveTenantId, isSuperAdmin]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    if (effectiveTenantId) {
      localStorage.setItem(`ugra_onboarding_done_${effectiveTenantId}`, '1');
    }
  };

  const navItems = useMemo(() => {
    const items = [];
    if (isSuperAdmin && !selectedTenantId) {
      items.push({ path: '/app/admin', label: 'Businesses', icon: Building2 });
      items.push({ path: '/app/customers', label: 'All Customers', icon: Users });
      items.push({ path: '/app/billing', label: t('nav.billing'), icon: DollarSign });
      items.push({ path: '/app/calendar', label: t('nav.calendar'), icon: Calendar });
      items.push({ path: '/app/tasks', label: 'Tasks', icon: CheckCircle2 });
      items.push({ path: '/app/activities', label: 'All Activities', icon: ScrollText, isBottom: true });
      items.push({ path: '/app/users-roles', label: 'All Users', icon: ShieldCheck, isBottom: true });
    } else {
      const plan = tenantData?.plan || 'free';
      const hasPaid = plan === 'pro' || plan === 'premium';
      const isPremium = plan === 'premium';
      items.push({ path: '/app', label: t('nav.overview'), icon: LayoutDashboard, proOnly: !hasPaid });
      items.push({ path: '/app/orders', label: t('nav.orders'), icon: ShoppingBag, proOnly: !hasPaid });
      items.push({
        key: 'menu-group',
        label: 'Ürünler & Menü',
        icon: Utensils,
        children: [
          { path: '/app/menu/products', label: 'Ürünler', exact: true },
          { path: '/app/menu/editor', label: 'Menü Editörü', exact: true },
        ]
      });
      items.push({
        key: 'campaigns-group',
        label: 'İndirimler & Ödüller',
        icon: Sparkles,
        children: [
          { path: '/app/campaigns', label: 'İndirimler & Kampanyalar', exact: true, proOnly: !hasPaid },
          { path: '/app/campaigns/rewards', label: 'Ödüller', exact: true, proOnly: !hasPaid },
          { path: '/app/campaigns/coupons', label: 'Kuponlar', exact: true, proOnly: !hasPaid },
        ]
      });
      items.push({
        key: 'customers-group',
        label: t('nav.customers'),
        icon: Users,
        children: [
          { path: '/app/customers', label: 'Müşteri Listesi', exact: true, proOnly: !hasPaid },
          { path: '/app/customer-logs', label: 'Müşteri Kayıtları', exact: true, proOnly: !hasPaid },
        ]
      });
      items.push({ path: '/app/analytics', label: t('nav.analytics', 'Analitik'), icon: BarChart3, proOnly: !hasPaid });
      items.push({
        key: 'notifications-group',
        label: 'Bildirim & Duyurular',
        icon: Bell,
        children: [
          { path: '/app/notifications/bildirimler', label: 'Bildirimler', exact: true, proOnly: !hasPaid },
          { path: '/app/notifications', label: 'Duyurular', exact: true, proOnly: !hasPaid },
        ]
      });
      items.push({ path: '/app/inventory', label: t('nav.inventory'), icon: Package, proOnly: !hasPaid });
      items.push({ path: '/app/accounting', label: t('nav.accounting', 'Muhasebe'), icon: Calculator, proOnly: !hasPaid });
      items.push({
        key: 'settings-group',
        label: 'İşletme Ayarları',
        icon: Settings,
        children: [
          { path: '/app/settings', label: 'İşletme Profili', exact: true },
          ...(hasPaid ? [
            { path: '/app/settings/qr', label: 'QR Yönetimi', exact: true },
            { path: '/app/settings/loyalty', label: 'Sadakat Ayarları', exact: true },
            { path: '/app/settings/visits', label: 'Ziyaret Rolleri', exact: true },
            { path: '/app/settings/users', label: 'Kullanıcılar', exact: true },
            { path: '/app/settings/roles', label: 'Roller', exact: true },
          ] : []),
          ...(isPremium ? [
            { path: '/app/settings/integrations', label: 'Entegrasyonlar', exact: true },
          ] : []),
          { path: '/app/settings/payments', label: 'Abonelik & Ödeme', exact: true },
        ]
      });
      items.push({ path: '/app/support', label: 'Destek', icon: LifeBuoy, isBottom: true });
      items.push({ path: '/app/activities', label: t('nav.activities'), icon: ScrollText, proOnly: !hasPaid, isBottom: true });
    }
    return items;
  }, [userData, selectedTenantId, tenantData, hasPermission, t]);

  const handleOpenProfile = () => {
    setEditData({
      firstName: userData?.firstName || '',
      lastName: userData?.lastName || '',
      phone: userData?.phone || tenantData?.phone || '',
      businessName: tenantData?.name || '',
      address: tenantData?.address || ''
    });
    setIsEditing(false);
    setIsProfileModalOpen(true);
  };

  const handleSaveProfile = async () => {
    const confirmed = await showConfirm('Profil bilgilerini kaydetmek istediğinize emin misiniz?', {
      title: 'Profili Kaydet',
      confirmText: 'Kaydet',
      type: 'info'
    });
    if (!confirmed) return;

    setUpdateLoading(true);
    try {
      if (user?.uid) await supabase.from('users').update({ first_name: editData.firstName, last_name: editData.lastName, phone: editData.phone }).eq('id', user.uid);
      if (effectiveTenantId) await supabase.from('tenants').update({ name: editData.businessName, address: editData.address }).eq('id', effectiveTenantId);
      await logActivity(effectiveTenantId, userData?.email || user?.email, user?.uid, 'UPDATE_PROFILE', 'Updated profile information');
      setIsProfileModalOpen(false);
      showAlert('Profil bilgileri güncellendi.', 'success');
    } catch (err) { showAlert("Hata oluştu.", "error"); }
    finally { setUpdateLoading(false); }
  };

  const activePath = (item) => {
    if (!item.path) return false;
    if (item.exact) return location.pathname === item.path;
    return location.pathname === item.path || (item.path !== '/app' && location.pathname.startsWith(item.path));
  };

  const userInitials = useMemo(() => userData?.firstName ? (userData.firstName[0] + (userData.lastName?.[0] || '')).toUpperCase() : '??', [userData]);
  const displayName = useMemo(() => userData?.firstName ? `${userData.firstName} ${userData.lastName || ''}` : userData?.email?.split('@')[0], [userData]);

  // Full-screen loading while tenant data is loading
  if (!tenantData && !isSuperAdmin) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-main)',
        zIndex: 9999,
        gap: '1.5rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 32 32" style={{ borderRadius: '12px' }}>
            <rect width="32" height="32" rx="8" fill="#6f4e37"/>
            <g transform="translate(4, 4)">
              <path d="M6 4v6a6 6 0 0 0 12 0V4" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="4" y1="20" x2="20" y2="20" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </g>
          </svg>
          <span style={{ fontSize: '2.5rem', fontWeight: 600, color: 'var(--text-main)', fontFamily: 'var(--font-logo)', position: 'relative', top: '3px' }}>ugra</span>
        </div>
        <div style={{
          width: '32px', height: '32px',
          border: '3px solid var(--border)',
          borderTopColor: '#6f4e37',
          borderRadius: '50%',
          animation: 'ugraSpin 0.8s linear infinite',
        }} />
        <style>{`@keyframes ugraSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="layout-container">
      <style>{`
        .layout-container { display: flex; height: 100vh; height: 100dvh; position: relative; overflow: hidden; }
        .sidebar { width: 260px; height: 100%; background: var(--bg-sidebar); border-right: 1px solid var(--border); padding: 2rem 1rem; display: flex; flex-direction: column; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); z-index: 1000; box-shadow: 6px 0 30px rgba(0, 0, 0, 0.04); overflow-y: auto; overflow-x: hidden; overscroll-behavior: contain; }
        .sidebar.closed { width: 80px; padding: 2rem 0; }
        .sidebar.closed .nav-link { 
            width: 48px !important; 
            height: 48px !important; 
            margin: 4px auto !important; 
            justify-content: center; 
            padding: 0 !important; 
            gap: 0; 
            border-radius: 12px;
        }
        .sidebar.closed .logo-container { justify-content: center; padding: 0; }
        .sidebar.closed .logout-btn { justify-content: center; padding: 12px 0; gap: 0; }
        .logo-container { display: flex; align-items: center; gap: 12px; margin-bottom: 3rem; padding: 0 0.75rem; overflow: hidden; transition: all 0.3s; }
        .logo-icon { background: var(--primary); padding: 8px; border-radius: 12px; color: var(--text-on-primary); flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: all 0.3s; }
        .logo-text { font-weight: 800; font-size: 1.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; color: var(--text-main); flex: 1; min-width: 0; letter-spacing: -0.02em; }
        .nav { display: flex; flex-direction: column; gap: 8px; flex: 1; }
        .nav-link { display: flex; align-items: center; gap: 12px; padding: 12px 14px; text-decoration: none; color: var(--text-muted); border-radius: 14px; transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); white-space: nowrap; overflow: hidden; position: relative; }
        .nav-link:hover { background: rgba(var(--primary-rgb), 0.08); color: var(--primary); transform: translateX(4px); }
        .sidebar.closed .nav-link:hover { transform: translateX(0) scale(1.05); }
        .nav-link.active { background: var(--primary); color: var(--text-on-primary); box-shadow: 0 8px 16px rgba(var(--primary-rgb), 0.25); }
        .main-content { flex: 1; padding: 2.5rem; background: var(--bg-main); overflow-y: auto; min-width: 0; }
        .top-bar { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2.5rem; gap: 1rem; }
        .user-profile { display: flex; align-items: center; gap: 12px; padding: 8px 16px; background: var(--bg-card); border: 1px solid var(--border); border-radius: 50px; box-shadow: 0 2px 8px rgba(0,0,0,0.04); cursor: pointer; transition: all 0.2s; }
        .user-profile:hover { border-color: var(--primary); box-shadow: 0 4px 12px rgba(0,0,0,0.08); transform: translateY(-1px); }
        .avatar { width: 32px; height: 32px; border-radius: 50%; background: var(--primary); display: flex; align-items: center; justify-content: center; color: var(--text-on-primary); font-weight: bold; font-size: 0.875rem; }
        .logout-btn { margin-top: auto; background: transparent; color: var(--text-muted); display: flex; align-items: center; gap: 12px; padding: 12px 14px; border: none; width: 100%; justify-content: flex-start; font-size: 1rem; border-radius: 12px; transition: all 0.2s; cursor: pointer; }
        .logout-btn:hover { color: #ef4444; background: #fef2f2; }
        .sidebar-overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); z-index: 999; }
        .profile-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 3000; animation: fadeIn 0.3s ease; }
        .profile-modal { background: var(--bg-card); width: 100%; max-width: 480px; border-radius: 24px; padding: 2rem; border: 1px solid var(--border); box-shadow: var(--shadow-md); position: relative; color: var(--text-main); max-height: 90vh; overflow-y: auto; }
        .edit-input { width: 100%; padding: 10px 14px; border: 1px solid var(--border); border-radius: 10px; background: var(--bg-main); color: var(--text-main); font-size: 0.9375rem; font-weight: 500; transition: all 0.2s; }
        .edit-input:focus { border-color: var(--primary); outline: none; box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.1); }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .profile-item { display: flex; flex-direction: column; gap: 4px; margin-bottom: 1.25rem; }
        .profile-label { font-size: 0.75rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; }
        .profile-value { font-size: 1rem; font-weight: 600; }
        @media (max-width: 1024px) {
          .sidebar {
            position: fixed;
            top: 0;
            left: -300px;
            width: 280px;
            height: 100vh;
            height: 100dvh;
            overflow-y: auto;
            overflow-x: hidden;
            overscroll-behavior: contain;
            -webkit-overflow-scrolling: touch;
            padding: 1.25rem 1rem !important;
          }
          .sidebar .logo-container { margin-bottom: 1.5rem !important; }
          .sidebar.open { left: 0; box-shadow: 20px 0 50px rgba(0,0,0,0.15); }
          .sidebar-overlay.visible { display: block; }
          .main-content { padding: 1.25rem 1rem; }
          .top-bar { flex-wrap: nowrap; gap: 0.5rem; }
          .ai-chat-btn span { display: none; }
          .ai-chat-btn { padding: 10px 12px !important; }
          .user-profile .avatar + div { display: none; }
          .user-profile { padding: 6px; }
        }
        @media (max-width: 640px) {
          .main-content { padding: 1rem 0.75rem; }
          .top-bar { margin-bottom: 1.25rem; }
          .profile-modal-overlay { align-items: flex-end; }
          .profile-modal {
            max-width: 100% !important;
            width: 100% !important;
            border-radius: 24px 24px 0 0 !important;
            margin: 0 !important;
            padding: 1.5rem 1rem !important;
            max-height: 92vh;
          }
        }
      `}</style>

      <div className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`} onClick={() => setSidebarOpen(false)} />

      <Sidebar
        isOpen={isSidebarOpen} setIsOpen={setSidebarOpen}
        navItems={navItems} activePath={activePath}
        tenantData={tenantData} handleLogout={logout} t={t}
        onProClick={() => setShowProModal(true)}
        onShowGuide={!isSuperAdmin ? () => setShowOnboarding(true) : undefined}
      />

      <main className="main-content">
        <OrderAlert
          alert={newOrderAlert}
          onClose={() => setNewOrderAlert(null)}
          onView={() => { navigate('/app/orders'); setNewOrderAlert(null); }}
          t={t}
          tenantData={tenantData}
        />

        <TopBar
          isSidebarOpen={isSidebarOpen} setSidebarOpen={setSidebarOpen}
          selectedTenantId={selectedTenantId} resetTenant={resetTenant}
          navigate={navigate} t={t} theme={theme} toggleTheme={toggleMode}
          i18n={i18n} toggleLanguage={() => i18n.changeLanguage(i18n.language === 'tr' ? 'en' : 'tr')}
          userData={userData} tenantData={tenantData} setIsAIChatOpen={setIsAIChatOpen}
          handleOpenProfile={handleOpenProfile} userInitials={userInitials} displayName={displayName}
        />

        <ProfileModal
          isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)}
          isEditing={isEditing} setIsEditing={setIsEditing}
          editData={editData} setEditData={setEditData}
          handleSaveProfile={handleSaveProfile} updateLoading={updateLoading}
          userData={userData} user={user} tenantData={tenantData}
          displayName={displayName} userInitials={userInitials} t={t} navigate={navigate}
        />

        {isSuperAdmin && !selectedTenantId ? (
          <SuperAdminAISidebar isOpen={isAIChatOpen} onClose={() => setIsAIChatOpen(false)} />
        ) : (
          <AIChatSidebar isOpen={isAIChatOpen} onClose={() => setIsAIChatOpen(false)} />
        )}
        <Outlet />
      </main>

      {showProModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backdropFilter: 'blur(4px)' }} onClick={() => setShowProModal(false)}>
          <div className="card" style={{ maxWidth: '420px', width: '100%', padding: '2.5rem', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'rgba(var(--primary-rgb), 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
              <Sparkles size={28} color="var(--primary)" />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '0.75rem' }}>Pro Plana Yukseltin</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6, marginBottom: '2rem' }}>
              Bu ozellik Pro plana dahildir. Tum ozelliklere erisim icin planınızı yukseltin.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setShowProModal(false)} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid var(--border)', background: 'var(--bg-main)', fontWeight: 600, cursor: 'pointer' }}>Kapat</button>
              <button onClick={() => { setShowProModal(false); navigate('/checkout'); }} className="btn-primary" style={{ flex: 1, padding: '12px', borderRadius: '12px', fontWeight: 600 }}>Pro'ya Yukselt</button>
            </div>
          </div>
        </div>
      )}

      {showOnboarding && (
        <OnboardingGuide onComplete={handleOnboardingComplete} />
      )}
    </div>
  );
};

export default Layout;
